/**
 * Corbels and posts, drawn (claude-docs/2026-10-30-venus-full-line-design.md,
 * group 3a; the user, 2026-10-31: "yes, show as much as possible") — the one
 * rule the 3D view and the elevation read, from the same counted rows the
 * estimate bills (`run.extraMolding`).
 *
 * They go where an island's seating is: its SEATED face — the run's finished
 * back panel with the run's stools in front of it — under the counter that
 * overhangs it.
 *
 *   - corbels hang on the panel, their tops under the counter, evenly spaced
 *     along it (equal gaps, none at the very ends, where the posts stand);
 *   - posts stand at the counter's corners on the seated side, floor to
 *     counter, set back from its edges by its nosing (its tighter overhang
 *     past the island's ends — the other end may run on for seating), never
 *     back inside the island; more than two are spaced evenly between, one
 *     stands at the middle.
 *
 * A row is drawn only where it is billed (`resolveTrimProfile`, a vendor's in
 * its colour); a run with corbels or posts and no seated counter keeps them
 * counted only, with the reason. Molding pieces and the straight valance
 * board are never drawn. With several seated faces, the longest takes them.
 *
 * Pure; metres.
 */
const { familyOf } = require('../cabinet-runs/families');
const { familySizes } = require('../cabinet-runs/vendorSizes');
const { findRunByIdOrAlias } = require('../cabinet-runs/runIds');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');
const { resolveTrimProfile } = require('./profiles');
const { carrierOutlines } = require('./fronts');

const DRAWN = Object.freeze({ corbel: 'corbel', 'decorative-post': 'post' });
// a probe this far in front of the panel's face finds the counter over it
const PROBE_M = 0.01;

const NO_SEAT =
  "Corbels and posts are drawn under a seated island's counter; this run has none, so they are counted only";

const dot = (a, b) => a.x * b.x + a.z * b.z;

/** A box's frame: its centre, facing (local +Z), along (local +X). */
function frameOf(o) {
  const ry = o.rotation?.y || 0;
  return {
    ry,
    c: o.center,
    facing: { x: Math.sin(ry), z: Math.cos(ry) },
    along: { x: Math.cos(ry), z: -Math.sin(ry) },
  };
}
const toWorld = (f, x, z) => ({
  x: f.c.x + f.along.x * x + f.facing.x * z,
  z: f.c.z + f.along.z * x + f.facing.z * z,
});
const toLocal = (f, p) => {
  const d = { x: p.x - f.c.x, z: p.z - f.c.z };
  return { x: dot(d, f.along), z: dot(d, f.facing) };
};

/** The rows of a run that are drawn, each with the profile it bills. */
function drawnRows(run, family) {
  const vendor = !!familySizes(family);
  return (run.extraMolding || []).flatMap((row) => {
    const count = Math.max(0, Math.floor(row?.count || 0));
    if (count === 0 || (vendor && !row.colour)) return [];
    const { profile } = resolveTrimProfile(
      family,
      'piece',
      row.piece,
      vendor ? [row.colour] : []
    );
    const kind = profile && DRAWN[profile.pieceKind];
    if (!kind) return [];
    return [{ kind, profile, count, colourCode: vendor ? row.colour : null }];
  });
}

/**
 * The run's seated face — its back panel with the run's stools in front of
 * it — and the counter over it, or null.
 */
function seatedFaceOf(objects, inRun) {
  const stools = objects.filter((o) => inRun(o) && o.type === 'bar-stool');
  const faces = objects
    .filter((o) => inRun(o) && o.category?.backPanel && o.dimensions?.x > 0)
    .map((panel) => {
      const f = frameOf(panel);
      const half = panel.dimensions.x / 2;
      const seated = stools.some((s) => {
        const l = toLocal(f, s.center);
        return l.z > 0 && Math.abs(l.x) <= half;
      });
      if (!seated) return null;
      const probe = toWorld(f, 0, panel.dimensions.z / 2 + PROBE_M);
      const counter = objects.find(
        (o) =>
          inRun(o) &&
          o.type === 'countertop' &&
          carrierOutlines(o).some((poly) => isPointInsidePolygon(probe, poly))
      );
      return counter ? { panel, counter, f } : null;
    })
    .filter(Boolean);
  return (
    faces.sort((a, b) => b.panel.dimensions.x - a.panel.dimensions.x)[0] || null
  );
}

/** Evenly spaced positions across `[x0, x1]`, `n` of them, ends included. */
function spread(n, x0, x1) {
  if (n === 1) return [(x0 + x1) / 2];
  return Array.from({ length: n }, (_, i) => x0 + ((x1 - x0) * i) / (n - 1));
}

/** The paint a colour code is, on this line — null for none (a generic row). */
const paintOf = (family, code) =>
  (code && family?.offering?.paint?.find((p) => p.code === code)?.hex) || null;

/** Every corbel and post of one run on its seated face. */
function piecesOnFace(run, family, face, rows) {
  const { panel, counter, f } = face;
  const half = panel.dimensions.x / 2;
  const faceZ = panel.dimensions.z / 2;
  const floorY = panel.center.y - panel.dimensions.y / 2;
  const undersideY = counter.center.y - counter.dimensions.y / 2;
  const corners = carrierOutlines(counter)[0].map((p) => toLocal(f, p));
  const edgeZ = Math.max(...corners.map((p) => p.z));
  const xs = corners.map((p) => p.x);
  const counterX0 = Math.min(...xs);
  const counterX1 = Math.max(...xs);
  // the counter's nosing: its tighter overhang past the island's ends (the
  // other end may run on for seating) — the posts stand back from its edges
  // by that much, at its real corners
  const nosing = Math.max(0, Math.min(-half - counterX0, counterX1 - half));

  const placed = (kind, i, r, x, z, y, dims) => ({
    id: `${run.id}-${kind}-${i}`,
    runId: run.id,
    kind,
    profileId: r.profile.id,
    sku: r.profile.sku,
    colourCode: r.colourCode,
    // drawn in the row's colour; null — the island's own (a generic row)
    paintColor: paintOf(family, r.colourCode),
    ...(r.profile.shape ? { shape: r.profile.shape } : {}),
    center: { ...toWorld(f, x, z), y },
    dimensions: dims,
    rotation: { x: 0, y: f.ry, z: 0 },
    // the panel it is drawn against, and where along it
    carrierId: panel.id,
    alongM: x,
  });

  const each = (kind) =>
    rows
      .filter((r) => r.kind === kind)
      .flatMap((r) => Array.from({ length: r.count }, () => r));
  const corbels = each('corbel');
  const posts = each('post');
  const out = [];
  corbels.forEach((r, i) => {
    const { widthM: w, heightM: h, depthM: d } = r.profile;
    const x = -half + (2 * half * (i + 1)) / (corbels.length + 1);
    out.push(
      placed('corbel', i, r, x, faceZ + d / 2, undersideY - h / 2, {
        x: w,
        y: h,
        z: d,
      })
    );
  });
  const postW = Math.max(0, ...posts.map((r) => r.profile.widthM));
  spread(
    posts.length,
    counterX0 + nosing + postW / 2,
    counterX1 - nosing - postW / 2
  ).forEach((x, i) => {
    const r = posts[i];
    const { widthM: w, depthM: d } = r.profile;
    const height = undersideY - floorY;
    // under the counter's edge, but never back inside the island
    const z = Math.max(edgeZ - nosing - d / 2, faceZ + d / 2);
    out.push(
      placed('post', i, r, x, z, floorY + height / 2, { x: w, y: height, z: d })
    );
  });
  return out;
}

/**
 * Every drawn corbel and post in the room, and the runs that keep theirs
 * counted only.
 *
 * @param {Object} room  `objects`, `cabinetRuns`
 * @returns {{ pieces: DrawnPiece[], notes: { runId, reason }[] }}
 */
function drawnCountedPieces(room) {
  const objects = (room?.objects || []).filter((o) => o?.center);
  const pieces = [];
  const notes = [];
  for (const run of room?.cabinetRuns || []) {
    const family = familyOf(run);
    const rows = drawnRows(run, family);
    if (rows.length === 0) continue;
    const inRun = (o) => !!o.runId && !!findRunByIdOrAlias([run], o.runId);
    const face = seatedFaceOf(objects, inRun);
    if (!face) {
      notes.push({ runId: run.id, reason: NO_SEAT });
      continue;
    }
    pieces.push(...piecesOnFace(run, family, face, rows));
  }
  return { pieces, notes };
}

module.exports = { drawnCountedPieces };
